import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TablePagination,
  Paper,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { getEcommerceProducts } from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';

interface Product {
  id: string;
  name: string;
  stockCode: string;
  imageUrl?: string | null;
}

interface SimpleProductSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (products: Product[]) => void;
  companyId: string;
  selectedProductIds: string[];
  maxSelection?: number;
}

const SimpleProductSelectionModal: React.FC<SimpleProductSelectionModalProps> = ({
  open,
  onClose,
  onSelect,
  companyId,
  selectedProductIds,
  maxSelection = 20,
}) => {
  const { t } = useTranslation('salesSystem');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ✅ Modal açıldığında SQL'den SADECE e-ticarete açık ürünleri yükle
  useEffect(() => {
    if (open && companyId) {
      loadProducts();
      setSearchTerm('');
      setPage(0);
    }
  }, [open, companyId]);

  // Modal açılışında dışarıdan gelen seçili ürünleri senkronize et
  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set(selectedProductIds));
  }, [open, selectedProductIds]);

  // ✅ YENİ: SQL'den SADECE IsEcommerce = 1 olan ürünleri çek
  const loadProducts = async () => {
    setLoading(true);
    try {
      console.log('🛒 [PRODUCT MODAL] SQL\'den e-ticarete açık ürünler yükleniyor...');
      
      // SQL API'den sadece e-ticarete açık ürünleri çek
      const ecommerceProducts = await getEcommerceProducts(companyId);
      
      console.log('✅ [PRODUCT MODAL] Yüklenen ürün sayısı:', ecommerceProducts.length);
      
      // Backend'den gelen DTO'yu Product interface'ine map et
      const productList: Product[] = ecommerceProducts.map((item: any) => ({
        id: item.id,
        name: item.name || '',
        stockCode: item.stockCode || '',
        imageUrl: item.imageUrl || null,
      }));

      setProducts(productList);
    } catch (error) {
      console.error('❌ [PRODUCT MODAL] Ürünler yüklenirken hata:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Artık sadece arama terimine göre filtrele (zaten e-ticarete açık olanlar geldi)
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      return product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.stockCode.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [products, searchTerm]);

  const handleSelectProduct = (productId: string) => {
    const newSelection = new Set(selectedIds);
    
    if (newSelection.has(productId)) {
      newSelection.delete(productId);
    } else {
      if (newSelection.size < maxSelection) {
        newSelection.add(productId);
      }
    }
    
    setSelectedIds(newSelection);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const visibleProductIds = filteredProducts
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(p => p.id);
      
      const newSelection = new Set(selectedIds);
      visibleProductIds.forEach(id => {
        if (newSelection.size < maxSelection) {
          newSelection.add(id);
        }
      });
      setSelectedIds(newSelection);
    } else {
      const visibleProductIds = filteredProducts
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(p => p.id);
      
      const newSelection = new Set(selectedIds);
      visibleProductIds.forEach(id => newSelection.delete(id));
      setSelectedIds(newSelection);
    }
  };

  const handleSave = () => {
    const selectedProducts = products.filter(p => selectedIds.has(p.id));
    onSelect(selectedProducts);
  };

  const paginatedProducts = filteredProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const isAllSelected = paginatedProducts.length > 0 && 
    paginatedProducts.every(p => selectedIds.has(p.id));

  const isIndeterminate = paginatedProducts.some(p => selectedIds.has(p.id)) && 
    !isAllSelected;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="product-selection-modal-title"
    >
      <DialogTitle id="product-selection-modal-title">
        {t('ecommerceProductSelectionModal.title')}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('ecommerceProductSelectionModal.selectedCount', { selected: selectedIds.size, max: maxSelection })}
          </Typography>
          
          <TextField
            fullWidth
            size="small"
            placeholder={t('ecommerceProductSelectionModal.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          
          {selectedIds.size >= maxSelection && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('ecommerceProductSelectionModal.maxSelectionWarning', { max: maxSelection })}
            </Alert>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        disabled={selectedIds.size >= maxSelection && !isAllSelected}
                      />
                    </TableCell>
                    <TableCell>{t('ecommerceProductSelectionModal.table.image')}</TableCell>
                    <TableCell>{t('ecommerceProductSelectionModal.table.productName')}</TableCell>
                    <TableCell>{t('ecommerceProductSelectionModal.table.sku')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedProducts.map((product) => {
                    const isSelected = selectedIds.has(product.id);
                    const canSelect = selectedIds.size < maxSelection || isSelected;
                    
                    return (
                      <TableRow
                        key={product.id}
                        hover
                        role="checkbox"
                        selected={isSelected}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectProduct(product.id)}
                            disabled={!canSelect}
                          />
                        </TableCell>
                        <TableCell>
                          {product.imageUrl ? (
                            <Box
                              component="img"
                              src={product.imageUrl}
                              alt={product.name}
                              sx={{
                                width: 40,
                                height: 40,
                                objectFit: 'cover',
                                borderRadius: 1,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                backgroundColor: '#f5f5f5',
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              📦
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>{product.stockCode}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredProducts.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage={t('ecommerceProductSelectionModal.pagination.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>{t('ecommerceProductSelectionModal.actions.cancel')}</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={selectedIds.size === 0}
        >
          {t('ecommerceProductSelectionModal.actions.select', { count: selectedIds.size })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimpleProductSelectionModal;
